import * as colors from '../../00colors.js';
import * as plugins from '../../00plugins.js';

import { demoFunc } from './dees-dataview-statusobject.demo.js';
import {
  DeesElement,
  html,
  customElement,
  type TemplateResult,
  property,
  state,
  cssManager,
  css,
  type CSSResult,
} from '@design.estate/dees-element';

import * as tsclass from '@tsclass/tsclass';
import { DeesContextmenu } from '../../00group-overlay/dees-contextmenu/dees-contextmenu.js';
import { themeDefaultStyles } from '../../00theme.js';
import '../../00group-layout/dees-tile/dees-tile.js';

declare global {
  interface HTMLElementTagNameMap {
    'dees-dataview-statusobject': DeesDataviewStatusobject;
  }
}

@customElement('dees-dataview-statusobject')
export class DeesDataviewStatusobject extends DeesElement {
  public static demo = demoFunc;
  public static demoGroups = ['Data View'];

  @property({ type: Object }) accessor statusObject!: tsclass.code.IStatusObject;

  @state() accessor copied: boolean = false;

  public static styles = [
    themeDefaultStyles,
    cssManager.defaultStyles,
    css`
      :host {
        font-family: var(--dees-font-family);
      }

      dees-tile {
        color: var(--dees-color-text-primary);
        cursor: default;
      }

      .heading {
        display: flex;
        align-items: center;
        gap: 8px;
        height: 32px;
        padding: 0 16px;
      }

      h1 {
        display: block;
        margin: 0;
        padding: 0;
        font-size: 14px;
        font-weight: 500;
        letter-spacing: -0.01em;
        color: var(--dees-color-text-secondary);
        flex: 1;
      }

      .statusdot {
        height: 10px;
        width: 10px;
        border-radius: 50%;
        background: var(--dees-color-text-muted);
        margin: auto;
        box-shadow: 0 0 0 3px color-mix(in srgb, var(--dees-color-text-muted) 20%, transparent);
        transition:
          background-color var(--dees-transition-default) var(--dees-ease-standard),
          box-shadow var(--dees-transition-default) var(--dees-ease-standard);
      }

      .copyMain {
        font-size: 11px;
        font-weight: 500;
        background: var(--dees-color-bg-primary);
        border: 1px solid var(--dees-color-border-default);
        text-align: center;
        padding: 4px 10px;
        border-radius: var(--dees-radius-md);
        color: var(--dees-color-text-secondary);
        user-select: none;
        cursor: pointer;
        transition:
          background-color var(--dees-transition-fast) var(--dees-ease-standard),
          color var(--dees-transition-fast) var(--dees-ease-standard),
          border-color var(--dees-transition-fast) var(--dees-ease-standard);
      }

      .copyMain:hover {
        background: var(--dees-color-hover);
        border-color: var(--dees-color-border-strong);
        color: var(--dees-color-text-primary);
      }

      .copyMain:active {
        background: var(--dees-color-active);
        transform: scale(0.98);
      }

      .copyMain.copied {
        background: color-mix(in srgb, var(--dees-color-accent-success) 12%, transparent);
        border-color: var(--dees-color-text-success);
        color: var(--dees-color-text-success);
      }

      .statusdot.ok {
        background: var(--dees-color-text-success);
        box-shadow: 0 0 0 3px color-mix(in srgb, var(--dees-color-text-success) 20%, transparent);
      }

      .statusdot.not_ok {
        background: var(--dees-color-accent-error);
        box-shadow: 0 0 0 3px color-mix(in srgb, var(--dees-color-accent-error) 20%, transparent);
      }

      .statusdot.partly_ok {
        background: var(--dees-color-accent-warning);
        box-shadow: 0 0 0 3px color-mix(in srgb, var(--dees-color-accent-warning) 20%, transparent);
      }

      .detail {
        display: flex;
        align-items: center;
        gap: 10px;
        height: 36px;
        padding: 0 16px;
        border-bottom: 1px solid var(--dees-color-border-subtle);
        transition: background-color var(--dees-transition-fast) var(--dees-ease-standard);
        cursor: context-menu;
      }

      .detail:last-child {
        border-bottom: none;
      }

      .detail:hover {
        background: var(--dees-color-row-hover);
      }

      .detail .statusdot {
        margin: 0;
        flex-shrink: 0;
      }

      .detail .detailsText {
        display: flex;
        align-items: baseline;
        flex: 1;
        min-width: 0;
      }

      .detail .detailsText .label {
        font-size: 12px;
        font-weight: 500;
        color: var(--dees-color-text-secondary);
        letter-spacing: -0.01em;
        white-space: nowrap;
        flex-shrink: 0;
      }

      .detail .detailsText .value {
        font-size: 13px;
        font-family: var(--dees-font-family-mono);
        color: var(--dees-color-text-primary);
        margin-left: auto;
        text-align: right;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .bottomBar {
        color: var(--dees-color-text-muted);
        height: 28px;
        font-size: 11px;
        line-height: 28px;
        display: flex;
        justify-content: flex-end;
        align-items: center;
        padding: 0 16px;
        width: 100%;
        box-sizing: border-box;
      }

      .bottomBar .statusLabel {
        font-weight: 500;
      }
    `,
  ];

  render(): TemplateResult {
    return html`
      <dees-tile>
        <div slot="header" class="heading">
          <div class="statusdot ${this.statusObject?.combinedStatus}"></div>
          <h1>${this.statusObject?.name || 'No status object assigned'}</h1>
          <div class="copyMain ${this.copied ? 'copied' : ''}" @click=${this.handleCopyAsJson}>
            ${this.copied ? 'Copied!' : 'Copy JSON'}
          </div>
        </div>
        ${this.statusObject?.details?.map((detailArg) => {
          return html`
            <div
              class="detail"
              @contextmenu=${(event: MouseEvent) => {
                event.preventDefault();
                DeesContextmenu.openContextMenuWithOptions(event, [
                  {
                    name: 'Copy Value',
                    iconName: 'lucide:copy',
                    action: async () => {
                      await this.copyToClipboard(detailArg.value, 'Value');
                    },
                  },
                  {
                    name: 'Copy Key',
                    iconName: 'lucide:key',
                    action: async () => {
                      await this.copyToClipboard(detailArg.name, 'Key');
                    },
                  },
                  {
                    name: 'Copy Key:Value',
                    iconName: 'lucide:copy-plus',
                    action: async () => {
                      await this.copyToClipboard(`${detailArg.name}: ${detailArg.value}`, 'Key:Value');
                    },
                  },
                ]);
              }}
            >
              <div class="statusdot ${detailArg.status}"></div>
              <span class="detailsText">
                <span class="label">${detailArg.name}</span>
                <span class="value">${detailArg.value}</span>
              </span>
            </div>
          `;
        })}
        <div slot="footer" class="bottomBar">
          <div class="statusLabel">${this.statusObject?.lastUpdated
            ? `Last updated: ${new Date(this.statusObject.lastUpdated).toLocaleString()}`
            : ''}</div>
        </div>
      </dees-tile>
    `;
  }

  async firstUpdated() {}

  private async copyToClipboard(text: string, type: string = 'Text') {
    try {
      await navigator.clipboard.writeText(text);
      console.log(`${type} copied to clipboard`);
      // You could add visual feedback here if needed
    } catch (err) {
      console.error(`Failed to copy ${type}:`, err);
    }
  }

  private copiedResetTimeout: ReturnType<typeof setTimeout> | null = null;

  private async handleCopyAsJson() {
    if (!this.statusObject) return;

    try {
      await navigator.clipboard.writeText(JSON.stringify(this.statusObject, null, 2));
    } catch (err) {
      console.error('Failed to copy:', err);
      return;
    }
    this.copied = true;
    if (this.copiedResetTimeout) {
      clearTimeout(this.copiedResetTimeout);
    }
    this.copiedResetTimeout = setTimeout(() => {
      this.copied = false;
      this.copiedResetTimeout = null;
    }, 1500);
  }

  public async disconnectedCallback() {
    if (this.copiedResetTimeout) {
      clearTimeout(this.copiedResetTimeout);
      this.copiedResetTimeout = null;
    }
    await super.disconnectedCallback();
  }
}
